/**
 * Authentication against Supabase's GoTrue REST API — three endpoints, no SDK.
 *
 * WHY NO `supabase-js` (0.1.17, the bundle ceiling tripping as pre-committed in the
 * intake design): the SDK was **68 % of the bundle's source input** while this app uses
 * exactly three of its endpoints — password grant, refresh grant, logout. Four of its
 * five subsystems (`postgrest-js`: the Data API is disabled by D11; `storage-js`:
 * photos live on R2 by D12; `realtime-js`, `functions-js`: never adopted) shipped to
 * every phone as dead weight. The API surface of this module is UNCHANGED — the five
 * consumer files did not change by one line.
 *
 * WHY THE TOKEN IS READ PER REQUEST AND NEVER CAPTURED (unchanged rule): an expired
 * token with RLS in play produces an **empty 200**, which reads as "no data" rather
 * than "not you any more". `currentAccessToken()` refreshes when near expiry, at the
 * moment of use, single-flight.
 *
 * WHY NO ROLE OR TENANT IS READ CLIENT-SIDE (unchanged): the browser learns who it is
 * from `whoami`, which reads the database. The session here is only "am I signed in,
 * and what is the current bearer token".
 */

import { MemoryStorage, safeGet, safeRemove, safeSet, type StorageLike } from '@mononoke/shared';
import { localStore } from './browser.js';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; userId: string; email: string | undefined };

/**
 * WHAT PERSISTS — and, since 0.1.421, what does NOT (ADR 0019).
 *
 * ⚠️ THERE IS NO TOKEN IN THIS SHAPE ANY MORE. The refresh token lives in an httpOnly cookie the
 * API sets, unreachable by script; the access token lives in the module variable below and dies
 * with the tab. What is left here is a MARKER: who is signed in and until when. An XSS that reads
 * it learns the user's own id and address, which the page is already displaying to them.
 *
 * ⚠️ THE MARKER IS WHY THE APP STILL OPENS OFFLINE, and it is the reason this is not simply "hold
 * everything in memory". `initAuth` publishes signed-in from this alone, with NO network call —
 * exactly as it did when the token was here. Without it, every reload would need `/auth/refresh`,
 * which fails with no signal, and the offline shell would show a sign-in screen instead of the
 * cached catalogue. That is a built, tested tier (`e2e/offline-shell.spec.ts`, offline levels 1-2)
 * and moving the tokens must not cost it.
 *
 * The trade is stated plainly: offline, the app knows WHO you are and serves cached reads while
 * writes queue; it has no live credential until it can reach the API, which is the same position
 * it was in before with an expired token.
 */
interface Stored {
  /** Seconds since epoch, as GoTrue reports it. Governs when a refresh is due, not access. */
  expiresAt: number;
  userId: string;
  email: string | undefined;
}

/**
 * ⚠️ MEMORY ONLY. Never written to storage, never logged. This is the credential an XSS can still
 * take — for its remaining lifetime, and no longer. That reduction, from "hold the account
 * indefinitely" to "act until this expires", is the whole of what ADR 0019 buys.
 */
let accessToken: string | null = null;

/**
 * ⚠️ THE KEY IS BUMPED TO v2 BECAUSE THE SHAPE CHANGED INCOMPATIBLY. A v1 blob carries tokens this
 * code no longer reads; leaving the key alone would mean every existing session is validated
 * against the new shape, fails, and signs the person out — while their old tokens sat in
 * localStorage indefinitely, which is the thing being removed. A new key makes the old one dead
 * data, and `purgeLegacySession()` deletes it on the next boot.
 */
const STORAGE_KEY = 'mononoke.auth.v2';
const LEGACY_STORAGE_KEY = 'mononoke.auth.v1';

/**
 * Persistence through the storage module (raw localStorage is banned by lint, and the
 * ban caught this file's first draft — the rule keeps earning its keep). No working
 * storage degrades to MemoryStorage: the session works until reload and the
 * Diagnostics screen's `storageAvailable` line says why it did not survive one.
 */
const store: StorageLike = localStore ?? new MemoryStorage();
/** A token this close to expiry is refreshed rather than used — an in-flight request
 * must not outlive its own credential. */
const REFRESH_SLACK_MS = 30_000;
/** Freshness threshold for the empty-read rule; matches the previous implementation. */
const FRESH_SLACK_MS = 5_000;

let state: AuthState = { status: 'loading' };
const listeners = new Set<(next: AuthState) => void>();
/** Single-flight: concurrent callers of an expired token share one refresh request. */
let refreshing: Promise<Stored | null> | null = null;

/**
 * ⚠️ THIS NOW POINTS AT OUR API, NOT AT SUPABASE (ADR 0019). The browser no longer speaks to
 * GoTrue; it speaks to `/auth/*` on the API, which holds the refresh token in an httpOnly cookie.
 *
 * ⚠️ AND THAT MAKES `VITE_API_URL` LOAD-BEARING FOR AUTHENTICATION. The cookie is same-site only
 * because `atelier.` and `api.farahelbastani.com` share a registrable domain. Pointing this at
 * `mononoke-api.fly.dev` would make it third-party, and browsers would refuse it — every reload
 * would sign the user out, with nothing saying why. It is a build-time variable, so that failure
 * ships in a bundle rather than appearing in a log.
 *
 * Env reads are lazy so tests can stub them; Vite inlines them either way.
 */
function config(): { url: string } | null {
  const url = import.meta.env.VITE_API_URL as string | undefined;
  return url ? { url: url.replace(/\/+$/, '') } : null;
}

/** Absent config is a configuration failure, not a sign-out. Callers must distinguish. */
export function authConfigured(): boolean {
  return config() !== null;
}

function readStored(): Stored | null {
  const raw = safeGet(store, STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (typeof parsed.expiresAt !== 'number' || typeof parsed.userId !== 'string') {
      return null;
    }
    return parsed as Stored;
  } catch {
    // Unparseable stored state degrades to signed-out — visibly, at the sign-in
    // screen, not silently mid-session.
    return null;
  }
}

/**
 * ⚠️ DELETE THE OLD TOKENS RATHER THAN LEAVING THEM. Bumping the key stops this code READING them;
 * it does not remove them from the device, and a refresh token sitting in localStorage is exactly
 * what ADR 0019 is about. Called once at boot. Best-effort by the storage module's contract — a
 * device that cannot remove it is a device that could not have written it either.
 */
function purgeLegacySession(): void {
  safeRemove(store, LEGACY_STORAGE_KEY);
}

function writeStored(next: Stored | null): void {
  // Outcomes are explicit per the storage module's contract; a failed write means the
  // in-memory session still works until reload, and Diagnostics' storageAvailable line
  // is where a persistently failing device shows up.
  if (next === null) safeRemove(store, STORAGE_KEY);
  else safeSet(store, STORAGE_KEY, JSON.stringify(next));
}

function publish(next: AuthState): void {
  state = next;
  for (const listener of listeners) listener(next);
}

function toState(stored: Stored | null): AuthState {
  if (!stored) return { status: 'signed-out' };
  return { status: 'signed-in', userId: stored.userId, email: stored.email };
}

/**
 * The API's session body. ⚠️ THERE IS NO `refreshToken` FIELD, deliberately — the API puts it in
 * the cookie and never sends it here. If one ever appears in this type, the cookie has stopped
 * being the only place it lives.
 */
interface SessionBody {
  accessToken?: string;
  expiresAt?: number;
  userId?: string;
  email?: string;
}

/** Splits the answer: the token goes to memory, the marker goes to storage. */
function toStored(body: SessionBody): Stored | null {
  if (!body.accessToken || !body.userId || typeof body.expiresAt !== 'number') return null;
  accessToken = body.accessToken;
  return { expiresAt: body.expiresAt, userId: body.userId, email: body.email };
}

/**
 * One call to our auth proxy.
 *
 * ⚠️ `credentials: 'include'` IS THE WHOLE MECHANISM AND IS EASY TO DROP. Without it the browser
 * neither sends nor stores the refresh cookie, and the symptom is not an error — sign-in appears to
 * work and every reload signs the user out. The API answers with
 * `Access-Control-Allow-Credentials: true` (it has since the client-view work), and the pair only
 * functions together.
 */
async function authRequest(path: string, payload: object): Promise<Stored | null> {
  const conf = config();
  if (!conf) return null;
  const response = await fetch(`${conf.url}/auth/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(15_000),
  });
  if (!response.ok) return null;
  const stored = toStored((await response.json()) as SessionBody);
  if (stored) {
    writeStored(stored);
    publish(toState(stored));
  }
  return stored;
}

/** Refresh once, shared by every concurrent caller. A failed refresh signs out —
 * loudly, via the published state, not by letting requests limp on with a dead token. */
function refresh(): Promise<Stored | null> {
  refreshing ??= authRequest('refresh', {})
    .then((next) => {
      if (!next) {
        writeStored(null);
        publish({ status: 'signed-out' });
      }
      return next;
    })
    .catch(() => {
      // Network failure ≠ invalid session: keep the stored session, report no token
      // for THIS call, and let the next call retry. Signing out on a dead radio would
      // throw away a session that is still perfectly valid.
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/**
 * The session, with a LIVE access token if one can be had.
 *
 * ⚠️ THE `accessToken === null` ARM IS NEW AND IS THE COST OF ADR 0019. The token no longer
 * survives a reload, so the first call after one has a valid marker and no credential and must go
 * to the network — where before it read both from storage. Offline, that refresh fails and this
 * returns null, which is correct: the caller gets no token, the request queues, and the published
 * state stays SIGNED-IN so the offline shell still renders. Signing out on a dead radio would
 * throw away a session that is perfectly valid.
 */
async function usableStored(): Promise<Stored | null> {
  const stored = readStored();
  if (!stored) return null;
  const fresh = stored.expiresAt * 1000 - Date.now() > REFRESH_SLACK_MS;
  if (fresh && accessToken !== null) return stored;
  return refresh();
}

/**
 * TAKE THE SESSION OUT OF THE URL, IF GOTRUE PUT ONE THERE.
 *
 * After a confirmation link is followed, GoTrue redirects to our page with the new session in
 * the **hash fragment** — `#access_token=…&refresh_token=…&expires_at=…`. Nothing else in this
 * app has ever needed to read that, because password sign-in is the only way in.
 *
 * ⚠️ THE FRAGMENT IS ERASED IMMEDIATELY, WHETHER OR NOT IT PARSED. A bearer token sitting in
 * `window.location` is a token in the browser's history, in the next screenshot, and in
 * anything that logs a URL. `replaceState` rather than `pushState`: the confirmation URL must
 * not become a back-button destination that replays a spent link.
 *
 * ⚠️ AND AN ERROR IN THE FRAGMENT IS READ TOO. An expired or reused confirmation link comes back
 * as `#error=…&error_description=…`, and silently dropping it would leave somebody staring at a
 * sign-in form with no idea their link had gone stale.
 */
function adoptSessionFromUrl(): { adopted: boolean; error: string | null } {
  const raw = window.location.hash.replace(/^#/, '');
  if (raw === '') return { adopted: false, error: null };

  const params = new URLSearchParams(raw);
  const access = params.get('access_token');
  const refresh = params.get('refresh_token');
  const error = params.get('error_description') ?? params.get('error');
  // Only touch the URL when the fragment is actually ours — other things use fragments.
  if (access === null && error === null) return { adopted: false, error: null };

  const clean = window.location.pathname + window.location.search;
  window.history.replaceState(null, '', clean);

  if (access === null || refresh === null) return { adopted: false, error };

  const expiresAtRaw = params.get('expires_at');
  const expiresInRaw = params.get('expires_in');
  const expiresAt =
    expiresAtRaw !== null && expiresAtRaw !== ''
      ? Number(expiresAtRaw)
      : Math.floor(Date.now() / 1000) + (expiresInRaw !== null ? Number(expiresInRaw) : 3600);
  if (!Number.isFinite(expiresAt)) return { adopted: false, error };

  /**
   * ⚠️ THE REFRESH TOKEN IS HANDED STRAIGHT TO THE API AND KEPT NOWHERE HERE (ADR 0019).
   *
   * GoTrue puts it in the fragment and there is no way to stop it, so for one instant it IS in
   * JavaScript — the very thing this design removes. It cannot be avoided; it can only be made
   * brief. The API exchanges it (GoTrue rotates on use, so the token that reaches the cookie is a
   * fresh one this URL never contained) and returns a session. Nothing is written to storage on
   * this path except the marker `toStored` produces.
   *
   * ⚠️ THE FRAGMENT IS ALREADY ERASED by the time this runs — `replaceState` above, before the
   * exchange rather than after, so a failed exchange still does not leave a token in history.
   */
  void adoptViaApi(refresh);
  return { adopted: true, error: null };
}

/**
 * The exchange. Fire-and-forget from `adoptSessionFromUrl`, which is synchronous by contract — it
 * is called before anything else at boot and must erase the fragment without awaiting a network
 * round trip. `initAuth` awaits the settled state through `adoptionSettled` below.
 */
let adoptionSettled: Promise<void> = Promise.resolve();

async function adoptViaApi(refreshToken: string): Promise<void> {
  adoptionSettled = (async () => {
    const stored = await authRequest('adopt', { refreshToken }).catch(() => null);
    if (!stored) {
      // A spent or tampered link. Signed-out is the honest state, and `authRedirectError` is what
      // the join screen renders — a silent failure here would be a blank sign-in form after
      // following a link that looked fine.
      redirectError = 'That link is no longer valid. Ask for a new invitation.';
      publish({ status: 'signed-out' });
    }
  })();
  await adoptionSettled;
}

/**
 * ⚠️ `decodeJwtPayload` STOOD HERE AND IS GONE (0.1.421). It read the subject out of the access
 * token in the URL fragment, because the old stored shape needed a user id the fragment does not
 * carry. The API's adopt exchange now returns `userId` from GoTrue's own answer, so an unverified
 * client-side decode is no longer part of the boot path at all — a small thing, but it was the one
 * place this app read a token's claims without verification, and it no longer does.
 */

/** Set when a confirmation link came back with a failure instead of a session. */
let redirectError: string | null = null;
/** Set when this page load adopted a session from the confirmation redirect. */
let adoptedFromUrl = false;

/** What went wrong on the way back from a confirmation link, if anything. Read by the join
 *  screen so an expired link says so rather than looking like a dead button. */
export function authRedirectError(): string | null {
  return redirectError;
}

/**
 * Did THIS page load arrive from a confirmation link, session in hand?
 *
 * ⚠️ THE JOIN SCREEN USES THIS TO PRESS ITS OWN BUTTON, and the narrowness is what makes that
 * safe. A signed-in visitor on `/join` might be anybody — most likely an admin in the wrong tab,
 * which is the 9 Aug field report — so auto-redeeming for them would fire a refusal at somebody
 * who pressed nothing. But a session adopted from the URL fragment means the person HERE, NOW,
 * just confirmed the address the account was created with; redeeming is the only thing they can
 * have come to do, and asking them to press the one button on the page is ceremony.
 */
export function authSessionAdoptedFromUrl(): boolean {
  return adoptedFromUrl;
}

/** Call once at boot. Idempotent. Resolves when the stored session has been read. */
export async function initAuth(): Promise<AuthState> {
  // Old tokens are removed on the first boot after the upgrade — bumping the key stops this code
  // reading them, not the device holding them, and holding them is the thing being fixed.
  purgeLegacySession();
  if (!authConfigured()) {
    publish({ status: 'signed-out' });
    return state;
  }

  /**
   * ⚠️ BEFORE THE STORED SESSION IS READ, and the order matters. Somebody confirming an
   * invitation may already have a stale session in storage from a previous attempt; the one
   * that just arrived in the URL is newer by definition and must win.
   */
  const fromUrl = adoptSessionFromUrl();
  redirectError = fromUrl.error;
  adoptedFromUrl = fromUrl.adopted;
  if (fromUrl.adopted) {
    // The exchange is a network call the synchronous adopter could not await; boot waits here so
    // callers of initAuth() see a settled state rather than 'loading'.
    await adoptionSettled;
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) publish(toState(readStored()));
    });
    return state;
  }

  const stored = readStored();
  if (!stored) {
    publish({ status: 'signed-out' });
  } else if (stored.expiresAt * 1000 - Date.now() > REFRESH_SLACK_MS) {
    publish(toState(stored));
  } else {
    publish(toState(await refresh()));
  }

  // Another tab signing in or out updates this one — parity with what supabase-js did.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) publish(toState(readStored()));
  });
  return state;
}

export function authState(): AuthState {
  return state;
}

export function subscribeAuth(listener: (next: AuthState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}

/** The current bearer token, refreshed if close to expiry. Null is a normal state. */
export async function currentAccessToken(): Promise<string | null> {
  if (!authConfigured()) return null;
  await usableStored();
  return accessToken;
}

/**
 * Is the stored session still usable? Asked BEFORE trusting an empty read of data that
 * should exist — an expired session and an empty table are indistinguishable at the
 * HTTP layer (standing rule).
 */
export async function sessionIsFresh(): Promise<boolean> {
  if (!authConfigured()) return false;
  const stored = await usableStored();
  if (!stored) return false;
  return stored.expiresAt * 1000 - Date.now() > FRESH_SLACK_MS;
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!authConfigured()) {
    return { ok: false, message: 'Sign-in is not configured in this build.' };
  }
  try {
    const stored = await authRequest('signin', { email, password });
    if (!stored) {
      // Deliberately not distinguishing "no such account" from "wrong password" —
      // that difference is an account-enumeration oracle. Unchanged from day one.
      return { ok: false, message: 'That email and password did not match an account.' };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: 'Could not reach the sign-in service. Try again.' };
  }
}

/**
 * CREATE AN ACCOUNT — the join screen's first step, and the only caller this will ever have.
 *
 * ⚠️ AN ACCOUNT IS NOT ACCESS, and this function is safe precisely because of that. Guardrail 6
 * as amended (masterplan v3.11): creating an authentication account is permitted and grants
 * NOTHING. `resolveIdentity` refuses any subject with no row in `public.users`, on every
 * request and every route, so what comes back from here can authenticate against Supabase all
 * day and read nothing. The invitation is the bridge, and it is redeemed separately.
 *
 * ⚠️ IT DELIBERATELY DOES NOT SIGN ANYONE IN, because with `Confirm email` ON — which §2 of the
 * self-service spec requires, or an attacker could squat an invitee's address before they
 * arrive — GoTrue returns a user and NO session. Returning `{ ok: true }` here means "go and
 * read your mail", not "you are in". A version of this that reported a session would be wrong
 * on the atelier's real configuration and right only on a misconfigured one.
 *
 * ⚠️ SIGN-UP MAY BE TURNED OFF ON THE PROJECT, and it is today: stage 3 flips that toggle last,
 * on purpose. GoTrue answers 422 `signup_disabled`, which is a configuration state rather than
 * a user error, so it gets its own sentence — otherwise the first person to try would be told
 * their perfectly good email address was the problem.
 */
/**
 * `accountExists` is the one failure the CALLER can act on rather than just report: the join
 * screen switches itself to its sign-in half instead of leaving somebody to find the button.
 * Everything else is a sentence and nothing more.
 */
export interface SignUpFailure {
  ok: false;
  message: string;
  accountExists?: true;
}

export async function signUp(
  email: string,
  password: string,
  /**
   * ⚠️ WHERE THE CONFIRMATION LINK LANDS THEM — added 10 Aug 2026 after the two-visit flow was
   * walked for real and found tedious. GoTrue takes it as `?redirect_to=` on the signup call and
   * puts it in the confirmation email, so confirming returns them to THIS page, invitation token
   * and all, with a session in the URL fragment. `initAuth` adopts it and the Join button is
   * simply there.
   *
   * ⚠️ IT MUST BE ON SUPABASE'S REDIRECT-URLS ALLOW-LIST or GoTrue silently substitutes the Site
   * URL and the token is lost. That failure is survivable rather than dead-ending — they land
   * signed in with no seat, which since 0.1.213 is a screen that says to open the invitation
   * link — but it is the reason this is passed rather than assumed, and RUNBOOK §2g lists the
   * console entry.
   */
  redirectTo?: string,
): Promise<{ ok: true } | SignUpFailure> {
  const conf = config();
  if (!conf) return { ok: false, message: 'Sign-up is not configured in this build.' };
  try {
    /**
     * Through the API proxy since 0.1.421 (ADR 0019), so this module has ONE configuration. The
     * upstream body is forwarded verbatim by that route, which is what keeps the `error_code`
     * mapping below working — it is read from GoTrue's own response and was got backwards once
     * already (10 Aug 2026). `redirect_to` moves into the payload rather than the query string.
     */
    const response = await fetch(`${conf.url}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, redirectTo }),
      signal: AbortSignal.timeout(15_000),
    });
    if (response.ok) return { ok: true };

    /**
     * ⚠️ EVERY SPECIFIC CODE IS CHECKED BEFORE ANY STATUS, AND THE FIRST VERSION OF THIS GOT IT
     * BACKWARDS — reported from use, 10 Aug 2026.
     *
     * It read `if (error_code === 'user_already_exists' || response.status === 422)`, placed
     * ABOVE the `weak_password` branch. GoTrue answers **422 for several different validation
     * failures**, weak passwords and malformed addresses among them, so the catch-all swallowed
     * them and every one came back as *"That address already has an account."*
     *
     * The damage is not a clumsy sentence, it is a wrong instruction: the account was never
     * created, and the reader was told to go and sign in to it. That is precisely what happened
     * — no confirmation email arrived, because no account was made, and then signing in failed
     * too. **Both halves of the symptom came from this one line.**
     *
     * ⚠️ AND THE COMMENT ABOVE IT SAID *"classified by CODE, never by message substring"* while
     * the code classified by STATUS as a catch-all. That is the same mistake in a different
     * suit: a coarse signal standing in for a specific one. A status may only narrow a decision
     * after every code that carries real meaning has been read.
     */
    const body = (await response.json().catch(() => ({}))) as { error_code?: string; msg?: string };

    switch (body.error_code) {
      case 'signup_disabled':
        return {
          ok: false,
          message: 'This workspace is not accepting new accounts yet. Tell the person who invited you.',
        };
      case 'user_already_exists':
      case 'email_exists':
        return {
          ok: false,
          accountExists: true,
          message: 'That address already has an account — sign in with it, then accept the invitation.',
        };
      case 'weak_password':
        return { ok: false, message: 'That password is too short — use at least six characters.' };
      case 'email_address_invalid':
      case 'validation_failed':
        return { ok: false, message: 'That does not look like a valid email address.' };
      /**
       * ⚠️ THE ONE THAT LOOKS LIKE NOTHING HAPPENING. Supabase caps how many messages Auth may
       * send per hour, and a run of test invitations is the likeliest thing to hit it. Throttled
       * is indistinguishable from never-sent unless it is named, and the right advice — wait,
       * do not keep pressing — is the opposite of what somebody does when a button seems dead.
       */
      case 'over_email_send_rate_limit':
        return {
          ok: false,
          message: 'Too many emails have been sent from this workspace in the last hour. Wait a while and try again.',
        };
      default:
        break;
    }

    // Only now, with no meaningful code, does the status get to narrow anything — and it says
    // so honestly rather than guessing at a cause.
    if (response.status === 429) {
      return { ok: false, message: 'Too many attempts just now. Wait a moment and try again.' };
    }
    return {
      ok: false,
      message: 'That did not work. Check the address and password, or ask the person who invited you.',
    };
  } catch {
    return { ok: false, message: 'Could not reach the sign-up service. Try again.' };
  }
}

export async function signOut(): Promise<void> {
  const conf = config();
  const stored = readStored();
  // LOCAL SIGN-OUT IS UNCONDITIONAL AND FIRST — including dropping the in-memory token, which is
  // now a separate act from clearing storage and would be easy to forget. Leaving it set would
  // mean a "signed out" screen whose next request still succeeds.
  accessToken = null;
  writeStored(null);
  publish({ status: 'signed-out' });
  if (!conf || !stored) return;
  try {
    /**
     * ⚠️ THE COOKIE CAN ONLY BE CLEARED BY THE SERVER, which is what makes this call different
     * from the best-effort revocation it replaces. Before ADR 0019 a failure here left a refresh
     * token idling on GoTrue's own schedule while the browser had already forgotten it. Now the
     * browser CANNOT forget it — it never had it — so if this call does not land, the cookie stays
     * and the next visit silently signs back in. The API clears it before contacting Supabase
     * precisely so an upstream outage cannot cause that; this call still has to reach the API.
     */
    await fetch(`${conf.url}/auth/signout`, {
      method: 'POST',
      credentials: 'include',
      signal: AbortSignal.timeout(10_000),
    });
  } catch {
    // The local session is gone either way. The cookie may survive an offline sign-out and is
    // cleared on the next successful one — recorded rather than pretended away.
  }
}

/** Tests only. */
export function resetAuthForTests(): void {
  state = { status: 'loading' };
  listeners.clear();
  refreshing = null;
  /**
   * ⚠️ THE IN-MEMORY TOKEN MUST BE CLEARED HERE, and forgetting it is not a test-only bug.
   * Since 0.1.421 the credential lives in a module variable rather than in storage, so clearing
   * storage alone leaves a live token behind — which is exactly what a partially-reset module
   * would do in the app too. Caught by three tests reading a token a previous test had minted,
   * which is the cheap version of the same mistake.
   */
  accessToken = null;
  safeRemove(store, STORAGE_KEY);
  safeRemove(store, LEGACY_STORAGE_KEY);
}

/** Exposed for diagnostics: never rendered as authority, only as status. */
export function authStatusLine(): string {
  if (!authConfigured()) return 'OFF (no Supabase config in this build)';
  switch (state.status) {
    case 'loading':
      return 'reading stored session';
    case 'signed-out':
      return 'signed out';
    case 'signed-in':
      return `signed in as ${state.email ?? state.userId}`;
  }
}

/** Only for the freshness check above; not a public accessor for the token. */
export function hasStoredSession(): boolean {
  return readStored() !== null;
}
